import { z } from 'zod';
import { compareDates } from '../dates';
import { ACADEMIC_LEVELS, AcademicLevel } from '../enums';
import { emailSchema, isoDate, nonEmptyText, optionalText, passwordSchema, uuidSchema } from './common';
import { workSummarySchema } from './works';

/** Chave de modalidade (ex.: TESE, ARTIGO) — os tipos que o(a) orientando(a) pode cadastrar. */
const modalityKeys = z
  .array(z.string().trim().min(1).max(40).regex(/^[A-Z0-9_]+$/, 'Modalidade inválida'))
  .max(20)
  .transform((v) => [...new Set(v)]);

/** Convite de orientando(a) por link: sem instituição; define os tipos de trabalho permitidos. */
export const BATCH_INVITE_MAX = 50;
const academicLevel = z.enum(ACADEMIC_LEVELS as [AcademicLevel, ...AcademicLevel[]], { message: 'Nível inválido' });

export const createAdviseeInviteSchema = z.object({
  /** Legado (tipos de trabalho): o plano de atividades dispensa a escolha */
  allowedModalityKeys: modalityKeys.default([]),
  level: academicLevel.optional(),
  label: optionalText(120),
  /** Opcional: somente a conta deste e-mail poderá aceitar. */
  email: emailSchema.optional(),
  /** Modelo de atividades aplicado ao plano assim que o ingresso for informado (ex.: Básico Mestrado e Doutorado). */
  templateId: uuidSchema.optional(),
});
export type CreateAdviseeInviteInput = z.input<typeof createAdviseeInviteSchema>;

/** Convites em lote: um link de uso único por aluno(a), com o nível e o modelo em comum. */
export const batchAdviseeInvitesSchema = z
  .object({
    level: academicLevel.optional(),
    templateId: uuidSchema.optional(),
    invitees: z
      .array(z.object({ label: nonEmptyText(120, 'Nome'), email: emailSchema.optional() }))
      .min(1, 'Informe ao menos um(a) aluno(a)')
      .max(BATCH_INVITE_MAX, `No máximo ${BATCH_INVITE_MAX} convites por vez`),
  })
  .refine((v) => {
    const emails = v.invitees.map((i) => i.email).filter(Boolean);
    return new Set(emails).size === emails.length;
  }, { message: 'Há e-mails repetidos na lista', path: ['invitees'] });
export type BatchAdviseeInvitesInput = z.input<typeof batchAdviseeInvitesSchema>;

/** Instituição e programa do(a) orientando(a): texto livre, informativo. */
const profileText = z.string().trim().min(2, 'Informe ao menos 2 caracteres').max(200);

export const acceptAdviseeInviteSchema = z.object({
  token: z.string().min(20).max(200),
  /** Somente para quem ainda não tem conta */
  name: z.string().trim().min(2).max(120).optional(),
  email: emailSchema.optional(),
  password: z.string().min(10).max(128).optional(),
  /** Cadastro do(a) orientando(a) — obrigatórios para conta nova */
  institutionName: profileText.optional(),
  programName: profileText.optional(),
});
export type AcceptAdviseeInviteInput = z.infer<typeof acceptAdviseeInviteSchema>;

/** Orientação: nível e, pelo(a) orientador(a), o cadastro do(a) orientando(a) (nome, instituição e programa). */
export const updateAdvisingLinkSchema = z.object({
  allowedModalityKeys: modalityKeys.optional(),
  level: academicLevel.nullable().optional(),
  name: nonEmptyText(160, 'Nome').optional(),
  institutionName: profileText.nullable().optional(),
  programName: profileText.nullable().optional(),
});
export type UpdateAdvisingLinkInput = z.infer<typeof updateAdvisingLinkSchema>;

const proposedWorkBase = z.object({
  modalityId: uuidSchema,
  title: nonEmptyText(300, 'Título'),
  summary: workSummarySchema.default({}),
  startDate: isoDate,
  expectedEndDate: isoDate,
  scholarship: z
    .object({ agency: nonEmptyText(80, 'Agência'), endDate: isoDate })
    .nullable()
    .optional(),
});
const datesInOrder = (v: { startDate: string; expectedEndDate: string }) => compareDates(v.expectedEndDate, v.startDate) > 0;

/** Trabalho cadastrado pelo(a) orientando(a): fica aguardando a confirmação do(a) orientador(a). */
export const proposeWorkSchema = proposedWorkBase
  .extend({ linkId: uuidSchema })
  .refine(datesInOrder, { message: 'O término previsto deve ser posterior ao início', path: ['expectedEndDate'] });
export type ProposeWorkInput = z.infer<typeof proposeWorkSchema>;

/**
 * Atividade cadastrada pelo(a) orientador(a) para um(a) orientando(a): começa confirmada e, com um modelo,
 * já nasce com os pontos de controle (prazos encadeados a partir do início).
 */
export const advisorCreateWorkSchema = proposedWorkBase.extend({ templateId: uuidSchema.optional() }).refine(datesInOrder, {
  message: 'O término previsto deve ser posterior ao início',
  path: ['expectedEndDate'],
});
export type AdvisorCreateWorkInput = z.infer<typeof advisorCreateWorkSchema>;

/** Ajustes do(a) orientando(a) enquanto o trabalho aguarda confirmação (ex.: após ser devolvido). */
export const updateProposedWorkSchema = proposedWorkBase.refine(datesInOrder, {
  message: 'O término previsto deve ser posterior ao início',
  path: ['expectedEndDate'],
});
export type UpdateProposedWorkInput = z.infer<typeof updateProposedWorkSchema>;

export const workApprovalSchema = z
  .discriminatedUnion('decision', [
    z.object({
      decision: z.literal('CONFIRM'),
      /** Ajustes de datas pelo(a) orientador(a) ao confirmar (opcional) */
      startDate: isoDate.optional(),
      expectedEndDate: isoDate.optional(),
      /** Confirma mesmo com alertas (avisos) no cronograma; erros sempre bloqueiam. */
      acknowledgeWarnings: z.boolean().default(false),
      note: optionalText(2000),
    }),
    z.object({ decision: z.literal('RETURN'), note: nonEmptyText(2000, 'Comentário') }),
    z.object({ decision: z.literal('REJECT'), note: nonEmptyText(2000, 'Motivo') }),
  ]);
export type WorkApprovalInput = z.infer<typeof workApprovalSchema>;

/** Convite de orientador(a) pela superadministração (ganha um espaço de orientação próprio). */
export const createAdvisorInviteSchema = z.object({
  label: optionalText(120),
  email: emailSchema.optional(),
});
export type CreateAdvisorInviteInput = z.infer<typeof createAdvisorInviteSchema>;

export const acceptAdvisorInviteSchema = z.object({
  token: z.string().min(20).max(200),
  name: z.string().trim().min(2).max(120).optional(),
  email: emailSchema.optional(),
  password: z.string().min(10).max(128).optional(),
});
export type AcceptAdvisorInviteInput = z.infer<typeof acceptAdvisorInviteSchema>;

/**
 * Datas do título (ingresso e prazo). O(a) orientando(a) informa o ingresso no primeiro acesso; a orientação
 * edita e prorroga (pode passar do prazo regular, com justificativa).
 */
export const titleDatesSchema = z
  .object({
    startDate: isoDate,
    expectedEndDate: isoDate,
    justification: optionalText(2000),
  })
  .refine(datesInOrder, { message: 'O prazo deve ser posterior ao ingresso', path: ['expectedEndDate'] });
export type TitleDatesInput = z.infer<typeof titleDatesSchema>;

// ------------------------------------------------------------------ link de ingresso do(a) orientador(a)

/** Alfabeto do código do link de ingresso: sem 0/O, 1/I/L (ditado em sala, copiado à mão). */
export const JOIN_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const JOIN_CODE_LENGTH = 6;
/** Código digitado: sem espaços nem hífens e em maiúsculas. */
export const normalizeJoinCode = (code: string) => code.replace(/[\s-]/g, '').toUpperCase();
const joinSlug = z.string().trim().min(8).max(40).regex(/^[a-z0-9]+$/, 'Link inválido');
const joinCode = z
  .string()
  .transform(normalizeJoinCode)
  .pipe(z.string().length(JOIN_CODE_LENGTH, `O código tem ${JOIN_CODE_LENGTH} caracteres`));

export const joinCodeCheckSchema = z.object({ slug: joinSlug, code: joinCode });
export type JoinCodeCheckInput = z.input<typeof joinCodeCheckSchema>;

/** Situação do(a) aluno(a) que pede ingresso: nível, instituição, programa e datas do título (sem modelo de atividades). */
const joinProfile = z.object({
  slug: joinSlug,
  code: joinCode,
  name: nonEmptyText(120, 'Nome'),
  level: academicLevel,
  institutionName: profileText,
  programName: profileText,
  startDate: isoDate,
  expectedEndDate: isoDate,
});
export const joinRequestSchema = joinProfile.refine(datesInOrder, { message: 'O prazo deve ser posterior ao ingresso', path: ['expectedEndDate'] });
export type JoinRequestInput = z.input<typeof joinRequestSchema>;
export type JoinRequestData = z.output<typeof joinRequestSchema>;

/** Pedido de ingresso de quem ainda não tem conta: cria a conta (e-mail e senha) junto. */
export const joinSignupSchema = joinProfile
  .extend({ email: emailSchema, password: passwordSchema })
  .refine(datesInOrder, { message: 'O prazo deve ser posterior ao ingresso', path: ['expectedEndDate'] });
export type JoinSignupInput = z.input<typeof joinSignupSchema>;
export type JoinSignupData = z.output<typeof joinSignupSchema>;

/** Confirmação pela orientação: pode corrigir o nível e as datas e escolhe o modelo de atividades. */
export const approveJoinRequestSchema = z
  .object({
    level: academicLevel,
    startDate: isoDate,
    expectedEndDate: isoDate,
    justification: optionalText(2000),
    templateId: uuidSchema.optional(),
  })
  .refine(datesInOrder, { message: 'O prazo deve ser posterior ao ingresso', path: ['expectedEndDate'] });
export type ApproveJoinRequestInput = z.infer<typeof approveJoinRequestSchema>;

/** Recado do(a) orientador(a) aos orientandos (aviso após o login). Vazio ou null remove o recado. */
export const ADVISEE_NOTICE_MAX = 1000;
/** Em quantos logins cada orientando(a) vê o recado (null = em todos, até ser removido). */
export const ADVISEE_NOTICE_TIMES = [1, 2, 3, 5, 10] as const;
const ADVISEE_NOTICE_TIMES_MAX = 10;
export const adviseeNoticeSchema = z.object({
  text: z
    .string()
    .trim()
    .max(ADVISEE_NOTICE_MAX, `Até ${ADVISEE_NOTICE_MAX} caracteres`)
    .nullable()
    .transform((v) => (v ? v : null)),
  times: z.number().int().min(1, 'Ao menos 1 login').max(ADVISEE_NOTICE_TIMES_MAX, `Até ${ADVISEE_NOTICE_TIMES_MAX} logins`).nullable().default(null),
});
export type AdviseeNoticeInput = z.input<typeof adviseeNoticeSchema>;
export type AdviseeNoticeData = z.output<typeof adviseeNoticeSchema>;

/** Quantas vezes o recado aparece para cada orientando(a), em palavras (do ponto de vista da orientação). */
export function noticeTimesLabel(times: number | null) {
  if (times === null) return 'Em todos os logins, até eu remover';
  return times === 1 ? 'Só no próximo login' : `Nos próximos ${times} logins`;
}
